import { useRef, useState } from "react";
import { ExternalLink, FileUp, Lock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const KIND_LABEL = { verification: "Board lookup evidence", license: "License copy" };

const DocumentRow = ({ record, kind, canUpload, onChanged }) => {
  const field = kind === "verification" ? record.verification_document : record.license_document;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const input = useRef(null);

  async function upload(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const body = new FormData();
      body.append("document", file);
      await api.post(`/credential-intelligence/${record.id}/documents/${kind}`, body, { headers: { "Content-Type": "multipart/form-data" } });
      setError("");
      onChanged();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  }

  async function open() {
    setBusy(true);
    try {
      const response = await api.get(`/credential-intelligence/${record.id}/documents/${field.id}/link`);
      window.open(`${process.env.REACT_APP_BACKEND_URL}${response.data.path}`, "_blank", "noopener");
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="credential-doc-row" data-testid={`credential-doc-${kind}-${record.id}`}>
      <span className="credential-doc-label">{KIND_LABEL[kind]}{kind === "verification" ? " (required to mark Verified)" : " (optional)"}</span>
      {field ? (
        <button type="button" className="table-edit-button" data-testid={`open-credential-doc-${kind}-${record.id}`} disabled={busy} onClick={open}>
          <ExternalLink size={13} /> {field.original_filename} · expiring link
        </button>
      ) : (
        <span className="pairing-meta">Not uploaded</span>
      )}
      {canUpload && (
        <label className="table-edit-button credential-upload-button">
          <FileUp size={13} /> {field ? "Replace" : "Upload"} <input ref={input} data-testid={`upload-credential-doc-${kind}-${record.id}`} type="file" accept="application/pdf,image/png,image/jpeg,image/webp" hidden disabled={busy} onChange={upload} />
        </label>
      )}
      {error && <small className="form-message error-message" data-testid={`credential-doc-error-${kind}-${record.id}`}>{error}</small>}
    </div>
  );
};

export const CredentialDocuments = ({ record, canUpload, onChanged }) => {
  if (!record.documents_visible) {
    return <p className="pairing-meta" data-testid={`credential-docs-restricted-${record.id}`}><Lock size={12} /> Evidence visible to System Administrator and HR only{record.has_verification_document ? " · board lookup on file" : ""}.</p>;
  }
  return (
    <div className="credential-docs" data-testid={`credential-docs-${record.id}`}>
      <DocumentRow record={record} kind="verification" canUpload={canUpload} onChanged={onChanged} />
      <DocumentRow record={record} kind="license" canUpload={canUpload} onChanged={onChanged} />
    </div>
  );
};
